import React from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { Button, useScreenQuery } from 'playup-web-core';

import Pagination from '../../../components/Pagination';
import formatCurrency from '../../../utils/formatCurrency';
import paymentStatus from '../../constants/paymentStatus';
import { formatDate, formatTime } from '../../../utils/dateFormatters';

interface Props {
	withdrawals: Array<Withdrawal>;
	pagination: Array<any>;
}

const PendingWithdrawals: React.FC<Props> = (props) => {
	const { isSmall } = useScreenQuery();
	const renderDesktopWithdrawals = () => {
		return (
			<>
				<TableHeader>
					<TableHeaderItem>Transaction ID#</TableHeaderItem>
					<TableHeaderItem>Date & Time</TableHeaderItem>
					<TableHeaderItem>Amount</TableHeaderItem>
					<TableHeaderItem>Action</TableHeaderItem>
				</TableHeader>
				{props.withdrawals.map((withdrawal) => (
					<TableRow key={withdrawal.id}>
						<TableRowItem>
							<p>#{withdrawal.transaction_id}</p>
						</TableRowItem>
						<TableRowItem>
							<RowItemWrapper>
								<p>{formatDate(dayjs(withdrawal.created_at))}</p>
								<LightText isSmallScreen={false}>{formatTime(dayjs(withdrawal.created_at))}</LightText>
							</RowItemWrapper>
						</TableRowItem>
						<TableRowItem>
							<p>{formatCurrency(withdrawal.amount)}</p>
						</TableRowItem>
						<TableRowItem>
							<RowItemWrapper>
								<LightText isSmallScreen={false}>(Processing)</LightText>
							</RowItemWrapper>
						</TableRowItem>
					</TableRow>
				))}
			</>
		);
	};

	const renderMobileWithdrawals = () => {
		return (
			<>
				{props.withdrawals.map((withdrawal) => (
					<PendingWithdrawalCard key={withdrawal.id}>
						<IdAndDateSection>
							<p>Tx ID: #{withdrawal.transaction_id}</p>
							<DateWrapper>
								<p>{formatDate(dayjs(withdrawal.created_at))}</p>
								<LightText isSmallScreen={true}>{formatTime(dayjs(withdrawal.created_at))}</LightText>
							</DateWrapper>
						</IdAndDateSection>
						<AmountSection
							borderBottom={
								withdrawal.status.id === paymentStatus.Pending || withdrawal.status.id === paymentStatus.Processing
							}
						>
							<p>Amount:</p>
							<p>{formatCurrency(withdrawal.amount)}</p>
						</AmountSection>
						<StatusSection>
							<RightSideWrapper>
								<LightText isSmallScreen={true}>(Processing)</LightText>
							</RightSideWrapper>
						</StatusSection>
					</PendingWithdrawalCard>
				))}
			</>
		);
	};
	return (
		<>
			{props.withdrawals && props.withdrawals.length > 0 && (
				<WithdrawalsContainer>
					<Title isSmallScreen={isSmall}>Pending Withdrawals</Title>
					{isSmall ? renderMobileWithdrawals() : renderDesktopWithdrawals()}
					<Pagination links={props.pagination} smallScreen={isSmall} />
				</WithdrawalsContainer>
			)}
		</>
	);
};

export default PendingWithdrawals;

const WithdrawalsContainer = styled.div`
	width: 100%;
`;

const Title = styled.p<{ isSmallScreen: boolean }>`
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '16px' : '20px')};
	font-weight: 600;
	text-align: center;
	${({ isSmallScreen }) => isSmallScreen && 'padding: 20px 0 10px'};
`;

const TableHeader = styled.div`
	display: flex;
	margin-top: 20px;
	font-size: 16px;
	color: ${({ theme }) => theme.colors.lightGreyR};
`;

const TableHeaderItem = styled.div`
	flex: 1;
	padding: 12px 18px;
`;

const TableRow = styled.div`
	display: flex;
	font-size: 16px;
	font-weight: 600;
	color: ${({ theme }) => theme.colors.darkR};
	background-color: ${({ theme }) => theme.colors.white};
`;

const TableRowItem = styled.div`
	flex: 1;
	padding: 18px;
	display: flex;
	align-items: center;
`;

const RowItemWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const LightText = styled.p<{ isSmallScreen: boolean }>`
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '16px')};
	font-weight: normal;
	color: ${({ theme }) => theme.colors.greyR};
	margin-top: 5px;
`;

const CancelButton = styled(Button)`
	height: 35px;
`;

// Mobile view
const PendingWithdrawalCard = styled.div`
	display: flex;
	flex-direction: column;
	border-radius: 5px;
	background-color: ${({ theme }) => theme.colors.white};
	padding: 15px;
	font-size: 14px;
	color: ${({ theme }) => theme.colors.darkR};
`;

const IdAndDateSection = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`;

const DateWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
`;

const AmountSection = styled.div<{ borderBottom: boolean }>`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	padding-top: 10px;
	margin-top: 10px;
	border-top: 1px solid;
	${({ borderBottom }) => borderBottom && 'padding-bottom: 10px; margin-bottom: 10px; border-bottom: 1px solid;'}
	border-color: ${({ theme }) => theme.colors.lightGreyR};
	font-weight: 600;
`;

const StatusSection = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	text-align: right;
`;

const RightSideWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 50%;
`;

const ModalText = styled.p<{ isSmallScreen: boolean }>`
	text-align: center;
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '20px' : '30px')};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
`;

const ModalButton = styled(Button)`
	margin-top: 10px;
	height: 50px;
`;
